import { Stack, useMediaQuery, useTheme } from "@mui/material";
import { Button, Typography, WarningBox } from "@foodpilot/foods";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SsoLoginMode } from "src/pages/Authentication/ssoConnector";
import { Loading } from "src/components/utils/Loading";
import { useSnackbar } from "src/utils/useSnackbar";
import { PROVIDER_PROTOCOL_LABELS, readOauthMode } from "../ssoProviderModel";
import {
    AppParamPorts,
    forgetProviderButton,
    persistProviderButton,
    readProviderButton,
    saveProviderWithButton,
} from "../ssoSettingsDraft";
import { SsoProviderListItem, useSsoProviders } from "../useSsoProviders";
import { Section } from "./SsoSettingsFields";
import { ProviderDraft, SsoProviderFormDialog } from "./SsoProviderFormDialog";
import { SsoProviderDeleteDialog } from "./SsoProviderDeleteDialog";
import { SsoProviderRow } from "./SsoProviderRow";
import { SsoProvidersTable } from "./SsoProvidersTable";

type DialogState = { mode: "create" } | { mode: "edit"; item: SsoProviderListItem };

type SsoProvidersSectionProps = {
    apiOrigin: string;
    loginMode: SsoLoginMode;
    parameters: Record<string, string | undefined>;
    ports: AppParamPorts;
};

export const SsoProvidersSection = (props: SsoProvidersSectionProps) => {
    const { apiOrigin, loginMode, parameters, ports } = props;
    const theme = useTheme();
    const isWideEnoughForTable = useMediaQuery(theme.breakpoints.up("lg"));
    const { t } = useTranslation();
    const snackbar = useSnackbar();
    const { providers, failedProtocols, isLoading, isSaving, isDeleting, saveProvider, deleteProvider } =
        useSsoProviders();

    const [dialogState, setDialogState] = useState<DialogState | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<SsoProviderListItem | null>(null);
    const [serverIssues, setServerIssues] = useState<Record<string, string>>({});
    const [isSubmitting, setSubmitting] = useState(false);

    const existingProviders = (providers ?? []).map((item) => ({
        protocol: item.protocol,
        providerName: item.providerName,
    }));
    const editing = dialogState?.mode === "edit" ? dialogState.item : null;
    const storedButton =
        editing === null ? { logo: undefined } : readProviderButton(parameters, editing.protocol, editing.providerName);

    const closeDialog = () => {
        setDialogState(null);
        setServerIssues({});
    };

    const handleSubmit = async (draft: ProviderDraft) => {
        setServerIssues({});
        setSubmitting(true);

        const providerName = String(draft.values.providerName ?? "").trim();
        const outcome = await saveProviderWithButton({
            saveProvider: () => saveProvider(draft.protocol, editing?.id, draft.values),
            persistButton: () => persistProviderButton(draft.protocol, providerName, draft.button, storedButton, ports),
            refetch: ports.refetch,
        });

        setSubmitting(false);

        if (outcome.kind === "provider-rejected") {
            setServerIssues(outcome.violations);
            snackbar.setSnackbarProps({
                type: "error",
                message: t("settings.sso.providers.rejected", "The provider was rejected, see the form."),
            });
            return;
        }

        if (outcome.kind === "provider-failed") {
            snackbar.setSnackbarProps({ type: "error", message: t("common.error_occurred") });
            return;
        }

        snackbar.setSnackbarProps(
            outcome.kind === "button-failed" ?
                {
                    type: "warning",
                    message: t(
                        "settings.sso.providers.saved_without_button",
                        "Provider saved, but its login page button could not be updated.",
                    ),
                }
            :   { type: "success", message: t("settings.sso.providers.saved", "Provider saved") },
        );
        closeDialog();
    };

    const handleDelete = async () => {
        if (deleteTarget === null) return;

        try {
            await deleteProvider(deleteTarget);
            await forgetProviderButton(deleteTarget.protocol, deleteTarget.providerName, ports, parameters);
            snackbar.setSnackbarProps({
                type: "success",
                message: t("settings.sso.providers.deleted", "Provider deleted"),
            });
        } catch {
            snackbar.setSnackbarProps({ type: "error", message: t("common.error_occurred") });
        } finally {
            setDeleteTarget(null);
            await ports.refetch();
        }
    };

    return (
        <Section
            title={t("settings.sso.providers.section_title", "Identity providers")}
            description={t(
                "settings.sso.providers.section_description",
                "Every provider listed here gets its own button on the login page.",
            )}
        >
            <Stack gap={theme.spacing(1.5)}>
                {failedProtocols.length > 0 && (
                    <WarningBox>
                        <Typography variant="body-medium">
                            {t(
                                "settings.sso.providers.load_failed",
                                "{{protocols}} providers could not be loaded, so any configured for those protocols are missing from this list.",
                                { protocols: failedProtocols.map((p) => PROVIDER_PROTOCOL_LABELS[p]).join(", ") },
                            )}
                        </Typography>
                    </WarningBox>
                )}
                {isLoading && <Loading />}
                {providers !== undefined && providers.length === 0 && (
                    <Typography variant="small-medium" sx={{ color: theme.custom.grey[2000] }}>
                        {t("settings.sso.providers.empty", "No providers configured yet.")}
                    </Typography>
                )}
                {(providers ?? []).length > 0 &&
                    (isWideEnoughForTable ?
                        <SsoProvidersTable
                            providers={providers ?? []}
                            onEdit={(item) => setDialogState({ mode: "edit", item })}
                            onDelete={(item) => setDeleteTarget(item)}
                        />
                    :   <Stack
                            sx={{
                                border: `1px solid ${theme.custom.grey[500]}`,
                                borderRadius: theme.spacing(1.5),
                                overflow: "hidden",
                            }}
                        >
                            {(providers ?? []).map((item, index) => (
                                <SsoProviderRow
                                    key={`${item.protocol}/${item.id}`}
                                    isFirst={index === 0}
                                    protocol={item.protocol}
                                    mode={readOauthMode(item.protocol, item.raw.protocol)}
                                    providerName={item.providerName}
                                    buttonLabel={typeof item.raw.label === "string" ? item.raw.label : undefined}
                                    onEdit={() => setDialogState({ mode: "edit", item })}
                                    onDelete={() => setDeleteTarget(item)}
                                />
                            ))}
                        </Stack>)}
                <Button
                    variant="secondary"
                    onClick={() => setDialogState({ mode: "create" })}
                    sx={{ alignSelf: "flex-start" }}
                >
                    {t("settings.sso.providers.add", "Add provider")}
                </Button>
            </Stack>

            {dialogState !== null && (
                <SsoProviderFormDialog
                    key={editing === null ? "create" : `${editing.protocol}/${editing.id}`}
                    open
                    initialProtocol={editing?.protocol ?? "saml2"}
                    provider={editing?.raw}
                    storedLogo={storedButton.logo}
                    existingProviders={existingProviders}
                    apiOrigin={apiOrigin}
                    loginMode={loginMode}
                    isSaving={isSaving || isSubmitting}
                    serverIssues={serverIssues}
                    onClose={closeDialog}
                    onSubmit={handleSubmit}
                />
            )}

            <SsoProviderDeleteDialog
                open={deleteTarget !== null}
                providerName={deleteTarget?.providerName}
                isDeleting={isDeleting}
                onClose={() => setDeleteTarget(null)}
                onConfirm={() => void handleDelete()}
            />
        </Section>
    );
};
